import { useRef, useState, type ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { Avatar } from '@/components/data-display/avatar'
import { Drawer } from '@/components/overlays/drawer'
import { Button } from '@/components/primitives/button'
import { cn } from '@/lib/cn'

import { appIconPresets } from './app-icon-presets'

type IconPickerTab = 'presets' | 'upload'

interface CropState {
  focusX: number
  focusY: number
  zoom: number
}

const initialCrop: CropState = { focusX: 0.5, focusY: 0.5, zoom: 1 }
const outputSize = 256

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('app icon image failed to load'))
    image.src = src
  })
}

function imageUrlToDataUrl(url: string) {
  return fetch(url)
    .then((response) => {
      if (!response.ok) throw new Error('app icon preset failed to load')
      return response.blob()
    })
    .then(
      (blob) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () =>
            typeof reader.result === 'string'
              ? resolve(reader.result)
              : reject(new Error('app icon preset failed to encode'))
          reader.onerror = () => reject(new Error('app icon preset failed to encode'))
          reader.readAsDataURL(blob)
        }),
    )
}

async function createCroppedAppIcon(sourceUrl: string, crop: CropState) {
  const image = await loadImage(sourceUrl)
  const canvas = document.createElement('canvas')
  canvas.width = outputSize
  canvas.height = outputSize
  const context = canvas.getContext('2d')
  if (!context) throw new Error('canvas is unavailable')

  const normalizedZoom = Math.max(1, Math.min(3, crop.zoom))
  const cropSize = Math.min(image.naturalWidth, image.naturalHeight) / normalizedZoom
  const sourceX = Math.max(0, image.naturalWidth - cropSize) * Math.max(0, Math.min(1, crop.focusX))
  const sourceY =
    Math.max(0, image.naturalHeight - cropSize) * Math.max(0, Math.min(1, crop.focusY))

  context.drawImage(image, sourceX, sourceY, cropSize, cropSize, 0, 0, outputSize, outputSize)

  return canvas.toDataURL('image/png')
}

export function AdminAppIconPicker({
  inputId,
  name,
  onChange,
  value,
}: {
  inputId: string
  name: string
  onChange: (avatar: string | null) => void
  value: string | null
}) {
  const { t } = useTranslation('adminApps')
  const inputRef = useRef<HTMLInputElement | null>(null)
  const operationIdRef = useRef(0)
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<IconPickerTab>('presets')
  const [selectedAvatar, setSelectedAvatar] = useState<string | null>(value)
  const [uploadUrl, setUploadUrl] = useState<string | null>(null)
  const [crop, setCrop] = useState<CropState>(initialCrop)
  const [cropError, setCropError] = useState('')
  const [isCropping, setIsCropping] = useState(false)

  const resetDraft = () => {
    setSelectedAvatar(value)
    setUploadUrl(null)
    setCrop(initialCrop)
    setCropError('')
    setIsCropping(false)
    setTab('presets')
  }

  const handleOpenChange = (nextOpen: boolean) => {
    operationIdRef.current += 1
    if (nextOpen) resetDraft()
    setOpen(nextOpen)
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    const operationId = ++operationIdRef.current
    const reader = new FileReader()
    reader.onload = () => {
      if (operationId !== operationIdRef.current) return
      if (typeof reader.result !== 'string') return
      setUploadUrl(reader.result)
      setCrop(initialCrop)
      setCropError('')
    }
    reader.onerror = () => {
      if (operationId === operationIdRef.current) {
        setCropError(t('drawer.iconPicker.uploadReadFailed'))
      }
    }
    reader.readAsDataURL(file)
  }

  const confirmSelection = async () => {
    const operationId = ++operationIdRef.current

    if (tab === 'presets') {
      if (!selectedAvatar) return
      setIsCropping(true)
      setCropError('')
      try {
        const preset = appIconPresets.find((item) => item.avatar === selectedAvatar)
        const nextAvatar = preset ? await imageUrlToDataUrl(preset.avatar) : selectedAvatar
        if (operationId !== operationIdRef.current) return
        onChange(nextAvatar)
        handleOpenChange(false)
      } catch {
        if (operationId === operationIdRef.current) {
          setCropError(t('drawer.iconPicker.presetLoadFailed'))
        }
      } finally {
        if (operationId === operationIdRef.current) {
          setIsCropping(false)
        }
      }
      return
    }

    if (!uploadUrl) {
      inputRef.current?.click()
      return
    }

    setIsCropping(true)
    setCropError('')
    try {
      const nextAvatar = await createCroppedAppIcon(uploadUrl, crop)
      if (operationId !== operationIdRef.current) return
      onChange(nextAvatar)
      handleOpenChange(false)
    } catch {
      if (operationId === operationIdRef.current) {
        setCropError(t('drawer.iconPicker.cropFailed'))
      }
    } finally {
      if (operationId === operationIdRef.current) {
        setIsCropping(false)
      }
    }
  }

  return (
    <>
      <div className="flex justify-start">
        <button
          aria-label={t('drawer.iconPicker.open')}
          className="group relative inline-flex size-16 cursor-pointer items-center justify-center overflow-hidden rounded-xl border border-dashed border-[var(--color-panel-border)] bg-[rgba(26,28,25,0.04)] text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-primary)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]"
          id={inputId}
          onClick={() => handleOpenChange(true)}
          type="button"
        >
          {value ? (
            <Avatar alt={name} className="size-16 rounded-xl text-lg" name={name} src={value} />
          ) : (
            <CameraIcon />
          )}
          <span className="absolute inset-x-0 bottom-0 bg-black/55 py-0.5 text-center text-[10px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
            {t('drawer.iconPicker.change')}
          </span>
        </button>
      </div>

      <Drawer
        blockOutsideInteractions
        bodyClassName="overflow-y-auto px-5 py-5"
        footer={
          <>
            <Button onClick={() => handleOpenChange(false)} size="sm" variant="secondary">
              {t('drawer.iconPicker.cancel')}
            </Button>
            <Button
              disabled={tab === 'presets' ? !selectedAvatar : false}
              isLoading={isCropping}
              onClick={() => void confirmSelection()}
              size="sm"
            >
              {tab === 'upload' && !uploadUrl
                ? t('drawer.iconPicker.chooseFile')
                : t('drawer.iconPicker.confirm')}
            </Button>
          </>
        }
        modal={false}
        onCloseStart={() => {
          operationIdRef.current += 1
        }}
        onOpenChange={handleOpenChange}
        open={open}
        panelClassName="min-[1120px]:right-[640px]"
        size="sm"
        title={t('drawer.iconPicker.title')}
        trapFocus
      >
        <div className="grid gap-5">
          <div
            aria-label={t('drawer.iconPicker.tabsLabel')}
            className="grid grid-cols-2 rounded-lg bg-[var(--color-surface-muted)] p-1"
            role="tablist"
          >
            {(['presets', 'upload'] as const).map((item) => (
              <button
                aria-selected={tab === item}
                className={cn(
                  'h-9 rounded-md text-sm font-semibold transition-colors',
                  tab === item
                    ? 'bg-white text-[var(--color-text)] shadow-sm'
                    : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]',
                )}
                disabled={isCropping}
                key={item}
                onClick={() => {
                  setCropError('')
                  setTab(item)
                }}
                role="tab"
                type="button"
              >
                {t(`drawer.iconPicker.tabs.${item}`)}
              </button>
            ))}
          </div>

          {tab === 'presets' ? (
            <div className="grid gap-4" role="tabpanel">
              <p className="text-sm leading-6 text-[var(--color-text-muted)]">
                {t('drawer.iconPicker.presetDescription')}
              </p>
              <div className="grid grid-cols-3 gap-3">
                {appIconPresets.map((preset) => {
                  const selected = selectedAvatar === preset.avatar
                  return (
                    <button
                      aria-pressed={selected}
                      className={cn(
                        'grid justify-items-center gap-2 rounded-xl border p-2.5 text-center transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
                        selected
                          ? 'border-[var(--color-primary)] bg-[var(--color-primary-soft)] shadow-[0_0_0_1px_var(--color-primary)]'
                          : 'border-[var(--color-panel-border)] bg-white hover:border-[var(--color-primary)]',
                      )}
                      disabled={isCropping}
                      key={preset.id}
                      onClick={() => setSelectedAvatar(preset.avatar)}
                      type="button"
                    >
                      <img alt="" className="size-14 rounded-xl" src={preset.avatar} />
                      <span className="w-full truncate text-xs font-medium text-[var(--color-text)]">
                        {t(preset.labelKey)}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          ) : (
            <div className="grid gap-5" role="tabpanel">
              <p className="text-sm leading-6 text-[var(--color-text-muted)]">
                {t('drawer.iconPicker.uploadDescription')}
              </p>
              <input
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="sr-only"
                disabled={isCropping}
                onChange={handleFileChange}
                ref={inputRef}
                type="file"
              />
              <button
                className="mx-auto flex size-44 items-center justify-center overflow-hidden rounded-3xl border border-dashed border-[var(--color-panel-border)] bg-[var(--color-surface-muted)] text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
                disabled={isCropping}
                onClick={() => inputRef.current?.click()}
                type="button"
              >
                {uploadUrl ? (
                  <img
                    alt={t('drawer.iconPicker.cropPreview')}
                    className="size-full object-cover transition-transform duration-150"
                    src={uploadUrl}
                    style={{
                      objectPosition: `${String(crop.focusX * 100)}% ${String(crop.focusY * 100)}%`,
                      transform: `scale(${crop.zoom.toFixed(2)})`,
                      transformOrigin: `${String(crop.focusX * 100)}% ${String(crop.focusY * 100)}%`,
                    }}
                  />
                ) : (
                  <span className="grid justify-items-center gap-2 px-5 text-sm font-medium">
                    <CameraIcon />
                    {t('drawer.iconPicker.chooseFile')}
                  </span>
                )}
              </button>

              {uploadUrl ? (
                <div className="grid gap-4">
                  <CropRange
                    disabled={isCropping}
                    label={t('drawer.iconPicker.zoom')}
                    max={3}
                    min={1}
                    onChange={(zoom) => setCrop((current) => ({ ...current, zoom }))}
                    step={0.01}
                    value={crop.zoom}
                  />
                  <CropRange
                    disabled={isCropping}
                    label={t('drawer.iconPicker.horizontal')}
                    max={1}
                    min={0}
                    onChange={(focusX) => setCrop((current) => ({ ...current, focusX }))}
                    step={0.01}
                    value={crop.focusX}
                  />
                  <CropRange
                    disabled={isCropping}
                    label={t('drawer.iconPicker.vertical')}
                    max={1}
                    min={0}
                    onChange={(focusY) => setCrop((current) => ({ ...current, focusY }))}
                    step={0.01}
                    value={crop.focusY}
                  />
                  <Button
                    disabled={isCropping}
                    onClick={() => inputRef.current?.click()}
                    size="sm"
                    variant="secondary"
                  >
                    {t('drawer.iconPicker.chooseAnother')}
                  </Button>
                </div>
              ) : null}
            </div>
          )}

          {cropError ? (
            <p className="text-sm text-[var(--color-danger)]" role="alert">
              {cropError}
            </p>
          ) : null}
        </div>
      </Drawer>
    </>
  )
}

function CropRange({
  disabled,
  label,
  max,
  min,
  onChange,
  step,
  value,
}: {
  disabled?: boolean
  label: string
  max: number
  min: number
  onChange: (value: number) => void
  step: number
  value: number
}) {
  return (
    <label className="grid gap-2 text-sm font-semibold text-[var(--color-text)]">
      {label}
      <input
        className="h-2 w-full cursor-pointer accent-[var(--color-primary)]"
        disabled={disabled}
        max={max}
        min={min}
        onChange={(event) => onChange(Number(event.target.value))}
        step={step}
        type="range"
        value={value}
      />
    </label>
  )
}

function CameraIcon() {
  return (
    <svg aria-hidden className="size-6" fill="none" viewBox="0 0 24 24">
      <path
        d="M8.25 7.25 9.4 5.5h5.2l1.15 1.75H18A2.25 2.25 0 0 1 20.25 9.5v7A2.25 2.25 0 0 1 18 18.75H6A2.25 2.25 0 0 1 3.75 16.5v-7A2.25 2.25 0 0 1 6 7.25h2.25Z"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
      <circle cx="12" cy="13" r="3.25" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  )
}
